import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import breakpointsMinWidth from '@styling/breakpointsMinWidth';
import Loader from '@components/common/loader/nextUICircularLoader';
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from '@heroui/react';
import getToken from '@utility/getToken';
import userDocumentsController from '@utility/userDocumentsController';
import { FormatTypes } from '@constants/templateDictionaries';
import UserDocumentsControllerImage from '@utility/userDocumentsImageController';
import { AppKeys } from '@components/drive/content/tabs/apps/shared/data';
import {
  ACCEPTED_IMPORT_FILE_EXTENSIONS,
  ACCEPTED_IMPORT_FILE_TYPES,
  IMPORT_FILE_SIZE_LIMIT,
  IMPORT_FILE_SIZE_ERROR_MESSAGE,
  IMPORT_FILE_SUCCESS_MESSAGE,
  IMPORT_FILE_ERROR_MESSAGE,
  ACCEPTED_IMPORT_FILE_TYPES_ERROR_MESSAGE,
  richTextArray,
} from '../fileImportModalButtons';

import snackBarTypes from '../../../../../constants/snackBarTypes';
import resetHistoryUtil from '../EditSpace/EditorPane/RenderContent/PersonalInfo/resetHistoryUtil';
import handleFileUpload from '../handleFileUpload';
import checkValidFileSize from '../../../../../utility/checkValidFileSize';
import { checkValidFileType } from '../../../../IRIS/3-stage/common/common';

import SnackBarAlerts from '../../../../common/snackBarAlerts/snackBarAlerts';

import Actions from '../../../../../store/actions';
import { SET_RESUME_IMPORT_DATA } from '../../../../../store/actions/actionTypes/editResumeForm';
import wizardDefaultData from './wizardDefaultData';
import defaultEditedInputs from '../EditSpace/EditorPane/Pad/linkedInOptimizer/utils/defaultEditedInputs';

const BodyText = styled.span`
  font-size: 14px;
`;
const BoldText = styled.span`
  font-weight: bold;
`;

const ButtonWrapper = styled(Button)`
  color: #333d47;
  background-color: white;
  min-width: 0px !important;
  padding-left: 0px !important;
  padding-right: 0px !important;
  border-radius: 0px;
  font-size: 12px;
  @media ${breakpointsMinWidth.desktopMd} {
    font-size: 14px;
  }
`;

const TextSpan = styled.span`
  padding: 4px 6px;
  border-radius: 4px;
  display: block;
  &:hover {
    background: rgba(162, 162, 162, 0.13);
  }
`;
const UploadFile = styled.input`
  display: none;
`;

const ImportDocument = (props) => {
  const {
    documentId,
    formatType,
    resumeType,
    userId,
    selectedPlan,
    universityPlan,
    updateUserInfo,
    userPreferences,
    updateUserPreferences,
    importFileData,
    text,
    isStreamFinished,
    importStreamId,
  } = props;
  const importFileRef = useRef();

  const [isImportingDocument, setIsImportingDocument] = useState(false);
  const [snackBar, setSnackBar] = useState({
    showSnackBar: false,
    snackBarMessage: '',
    snackBarType: snackBarTypes.INFO,
    duration: null,
  });
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 767);
  const [currentImportStreamId, setCurrentImportStreamId] = useState(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const app = searchParams.get('app');

  const setSnackBarState = (
    showSnackBar,
    snackBarType,
    snackBarMessage,
    duration,
  ) => {
    setSnackBar({ showSnackBar, snackBarMessage, snackBarType, duration });
  };

  const handleUpdateUserPreferences = () => {
    updateUserPreferences({
      userPreferences: {
        ...userPreferences,
        layout: {
          ...userPreferences.layout,
          tetraScan: false,
          previewPad: true,
          tipsPad: false,
          veteranTips: false,
          galleryPad: false,
          galleryPadFullScreen: false,
          menuBar: true,
          linkedInOptimizer: false,
          contentGeneration: false,
          questionsGenerator: false,
          aiCareerPath: false,
        },
      },
      formatType: formatType || FormatTypes.RESUME,
    });
  };

  const handleImportFiles = async (e) => {
    const { files } = e.target;
    if (files?.length === 0) return;
    setIsImportingDocument(true);
    const isValidFile = checkValidFileType(
      files[0],
      ACCEPTED_IMPORT_FILE_TYPES,
    );

    if (!isValidFile) {
      setIsImportingDocument(false);
      setSnackBarState(
        true,
        snackBarTypes.ERROR,
        ACCEPTED_IMPORT_FILE_TYPES_ERROR_MESSAGE,
      );
      e.target.value = '';
      return;
    }
    const isValidFileSize = checkValidFileSize(
      files[0],
      IMPORT_FILE_SIZE_LIMIT,
    );

    if (!isValidFileSize) {
      setIsImportingDocument(false);
      setSnackBarState(
        true,
        snackBarTypes.ERROR,
        IMPORT_FILE_SIZE_ERROR_MESSAGE,
      );
      e.target.value = '';
      return;
    }
    try {
      handleUpdateUserPreferences();
      updateUserInfo({ isFullscreen: true });

      const id = uuidv4();
      setCurrentImportStreamId(id);
      await handleFileUpload({
        event: e,
        documentId,
        formatType,
        resumeType,
        userId,
        selectedPlan,
        universityPlan,
        currentImportStreamId: id,
      });
      e.target.value = '';
    } catch (error) {
      setIsImportingDocument(false);
      setSnackBarState(true, snackBarTypes.ERROR, IMPORT_FILE_ERROR_MESSAGE);
      e.target.value = '';
    }
  };

  useEffect(() => {
    setCurrentImportStreamId(importStreamId);
  }, []);

  useEffect(() => {
    const importResume = async () => {
      if (app === AppKeys.IMPORT_RESUME && !isStreamFinished) {
        setIsImportingDocument(true);
        try {
          handleUpdateUserPreferences();

          updateUserInfo({ isFullscreen: true });

          const id = uuidv4();
          setCurrentImportStreamId(id);
          const token = await getToken();

          const data = {
            documentId,
            formatType,
            resumeType,
            userId,
            fileData: importFileData,
            selectedPlan,
            universityPlan,
            currentImportStreamId: id,
          };
          await UserDocumentsControllerImage.importDocumentStream(token, data);
        } catch (error) {
          setIsImportingDocument(false);
          setSnackBarState(
            true,
            snackBarTypes.ERROR,
            IMPORT_FILE_ERROR_MESSAGE,
          );
        }
      }
    };

    importResume();
  }, []);

  const { textToShow, show } = wizardDefaultData;

  const handleUploadDocument = () => {
    if (importFileRef && importFileRef.current) {
      importFileRef.current.click();
    }
  };

  const handleCloseSnackBar = (e) => {
    if (e) e.stopPropagation();

    setSnackBar({ showSnackBar: false });
  };

  const { showSnackBar, snackBarMessage, snackBarType, duration } = snackBar;
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 767);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const {
      handleFormInitialState,
      updateEditedInputsLinkedInOptimizer,
      setImportFileData,
    } = props;
    let interval;
    if (isImportingDocument) {
      interval = setInterval(async () => {
        try {
          const token = await getToken();
          const parsedImportDocument =
            await userDocumentsController.getDocumentById(documentId, token);

          handleFormInitialState({
            documentData: {
              ...parsedImportDocument,
            },
            formatType,
            documentId,
            eventType: SET_RESUME_IMPORT_DATA,
            selectedPlan,
            universityPlan,
          });

          const isStreamDone = parsedImportDocument?.isStreamFinished;
          const importStreamIdFromDb = parsedImportDocument?.importStreamId;
          if (currentImportStreamId === importStreamIdFromDb && isStreamDone) {
            setIsImportingDocument(false);
            setSnackBarState(
              true,
              snackBarTypes.SUCCESS,
              IMPORT_FILE_SUCCESS_MESSAGE,
            );
            const {
              fullName,
              tagline,
              phoneNumber,
              email,
              address,
              objective,
              personalLinks,
              documentPicture,
              resetHistory,
            } = props;
            resetHistoryUtil(
              resetHistory,
              fullName,
              tagline,
              phoneNumber,
              email,
              address,
              objective,
              personalLinks,
              documentPicture,
            );
            updateEditedInputsLinkedInOptimizer({
              linkedInOptimizerEditedInputs: defaultEditedInputs,
            });
            clearInterval(interval);
            updateUserInfo({ isFullscreen: false });
            // Reset the import file data from the store, this was for apps
            if (app === AppKeys.IMPORT_RESUME) {
              setImportFileData({ fileData: '' });
            }
          }
        } catch (error) {
          setSnackBarState(
            true,
            snackBarTypes.ERROR,
            IMPORT_FILE_ERROR_MESSAGE,
          );
          clearInterval(interval);
          updateUserInfo({ isFullscreen: false });
          setIsImportingDocument(false);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isStreamFinished, isImportingDocument]);

  return (
    <>
      {showSnackBar && (
        <SnackBarAlerts
          showSnackBar={showSnackBar}
          handleClose={handleCloseSnackBar}
          snackBarMessage={snackBarMessage}
          snackBarType={snackBarType}
          duration={duration}
        />
      )}

      {isImportingDocument && isLargeScreen && (
        <Modal
          isOpen={show}
          isImportDocument
          isDismissable={false}
          isKeyboardDismissDisabled
          hideCloseButton
        >
          <ModalContent>
            <>
              <ModalBody>
                <Loader text={textToShow} />
              </ModalBody>
            </>
          </ModalContent>
        </Modal>
      )}

      {isLargeScreen && (
        <Modal isOpen={isOpen} onOpenChange={onOpenChange} hideCloseButton>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalBody className="py-6 pb-2">
                  <BodyText>
                    {richTextArray.map((item) =>
                      item.class === 'bold' ? (
                        <BoldText key={item.key}>{item.text}</BoldText>
                      ) : (
                        <span key={item.key}>{item.text}</span>
                      ),
                    )}
                  </BodyText>
                </ModalBody>
                <ModalFooter>
                  <Button
                    aria-label="Cancel Button"
                    radius="full"
                    className="min-w-16 h-8 text-xs"
                    color="default"
                    onPress={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    aria-label="Proceed Button "
                    radius="full"
                    className="min-w-16 h-8 text-xs"
                    color="primary"
                    onPress={() => {
                      handleUploadDocument();
                      onClose();
                    }}
                  >
                    Proceed
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      )}

      <ButtonWrapper
        onPress={onOpen}
        onMouseEnter={(e) => {
          e.stopPropagation();
        }}
        aria-label="Import existing document"
      >
        <UploadFile
          ref={importFileRef}
          type="file"
          accept={ACCEPTED_IMPORT_FILE_EXTENSIONS}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => {
            handleImportFiles(e);
          }}
        />
        <TextSpan>{text}</TextSpan>
      </ButtonWrapper>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    userId: state.editResumeFormReducer.userId,
    formatType: state.editResumeFormReducer.formatType,
    templateType: state.editResumeFormReducer.resumeType,
    documentId: state.editResumeFormReducer.documentId,
    fullName: state.editResumeFormReducer.fullName,
    tagline: state.editResumeFormReducer.tagline,
    phoneNumber: state.editResumeFormReducer.phoneNumber,
    email: state.editResumeFormReducer.email,
    personalLinks: state.editResumeFormReducer.personalLinks,
    address: state.editResumeFormReducer.address,
    objective: state.editResumeFormReducer.objective,
    resumeType: state.editResumeFormReducer.resumeType,
    isStreamFinished: state.editResumeFormReducer.isStreamFinished,
    fileImported: state.editResumeFormReducer.fileImported,
    selectedPlan: state.userInfoReducer.selectedPlan,
    universityPlan: state.userInfoReducer.universityPlan,
    userPreferences: state.editResumeFormReducer.userPreferences,
    importFileData: state.editResumeFormReducer.importFileData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleFormInitialState: (params) => {
      dispatch(Actions.editDocumentFormActions.setInitialDocumentData(params));
    },
    setFileImportStatus: (params) =>
      dispatch(Actions.editResumeFormActions.setFileImportStatus(params)),
    resetHistory: (params) =>
      dispatch(Actions.editResumeFormActions.resetHistory(params)),
    updateEditedInputsLinkedInOptimizer: (params) =>
      dispatch(
        Actions.editResumeFormActions.updateEditedInputsLinkedInOptimizer(
          params,
        ),
      ),
    setImportFileData: (params) =>
      dispatch(Actions.editResumeFormActions.setImportFileData(params)),
    updateUserInfo: (params) =>
      dispatch(Actions.userInfoActions.updateUserInfo(params)),
    updateUserPreferences: (params) =>
      dispatch(Actions.editDocumentFormActions.updateUserPreferences(params)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ImportDocument);
